(() => {
  const ROOT_SELECTOR = '[data-ramiu-product]';
  const featurePromises = new Map();

  const updateText = (element, value) => {
    if (element) element.textContent = value;
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const loadShopifyFeature = (name) => {
    if (featurePromises.has(name)) return featurePromises.get(name);

    const promise = new Promise((resolve, reject) => {
      if (!window.Shopify?.loadFeatures) {
        reject(new Error(`Shopify feature loader is unavailable for ${name}.`));
        return;
      }

      window.Shopify.loadFeatures([
        {
          name,
          version: '1.0',
          onLoad: (errors) => {
            if (errors) reject(errors);
            else resolve();
          },
        },
      ]);
    });

    featurePromises.set(name, promise);
    promise.catch(() => featurePromises.delete(name));
    return promise;
  };

  const pauseExternalVideo = (iframe) => {
    const host = iframe.closest('[data-media-host]')?.dataset.mediaHost;
    if (host === 'youtube') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      );
    } else if (host === 'vimeo') {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }
  };

  const pauseAllMedia = (root, exception = null) => {
    root.querySelectorAll('video').forEach((video) => {
      if (video !== exception) video.pause();
    });

    root.querySelectorAll('iframe').forEach((iframe) => {
      if (iframe !== exception) pauseExternalVideo(iframe);
    });

    root.querySelectorAll('[data-deferred-media]').forEach((container) => {
      if (!container.contains(exception)) container.modelViewerUI?.pause();
    });
  };

  const setupModelViewer = (container) => {
    const modelViewer = container.querySelector('model-viewer');
    if (!modelViewer) return;

    loadShopifyFeature('model-viewer-ui')
      .then(() => {
        if (window.Shopify?.ModelViewerUI && !container.modelViewerUI) {
          container.modelViewerUI = new window.Shopify.ModelViewerUI(modelViewer);
        }
      })
      .catch(() => {});
  };

  const activateDeferredMedia = (root, container, autoplay = true) => {
    pauseAllMedia(root);

    if (container.dataset.mediaLoaded !== 'true') {
      const button = container.querySelector('[data-deferred-media-button]');
      const template = container.querySelector('[data-deferred-media-template]');
      if (!button || !template) return;

      button.replaceWith(template.content.cloneNode(true));
      container.dataset.mediaLoaded = 'true';
    }

    const video = container.querySelector('video');
    const modelViewer = container.querySelector('model-viewer');

    if (video && autoplay) video.play().catch(() => {});
    if (modelViewer) setupModelViewer(container);
  };

  const setActiveThumbnail = (root, mediaId) => {
    root.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      if (thumbnail.dataset.targetMediaId === mediaId) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });
  };

  const announceMedia = (root, media) => {
    const status = root.querySelector('[data-media-status]');
    if (!status || !media) return;

    const position = media.dataset.mediaPosition;
    const total = root.querySelectorAll('[data-media-id]').length;
    const type = media.dataset.mediaType === 'image' ? 'Image' : 'Media';
    status.textContent = `${type} ${position} of ${total} ready.`;
  };

  const selectMedia = (
    root,
    mediaId,
    { activate = true, scroll = true, announce = true } = {}
  ) => {
    if (!mediaId) return null;

    const media = root.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!media) return null;

    pauseAllMedia(root);
    setActiveThumbnail(root, mediaId);

    const deferredMedia = media.querySelector('[data-deferred-media]');
    if (activate && deferredMedia) activateDeferredMedia(root, deferredMedia);

    if (scroll) {
      window.requestAnimationFrame(() => {
        media.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      });
    }

    if (announce) announceMedia(root, media);
    return media;
  };

  const initializeShopifyXR = (root) => {
    const modelData = root.querySelector('[data-product-models]');
    if (!modelData || root.dataset.shopifyXrInitialized === 'true') return;

    let models;
    try {
      models = JSON.parse(modelData.textContent);
    } catch (_error) {
      return;
    }

    const setupXR = () => {
      if (!window.ShopifyXR) {
        document.addEventListener('shopify_xr_initialized', setupXR, { once: true });
        return;
      }

      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
      root.dataset.shopifyXrInitialized = 'true';
    };

    loadShopifyFeature('shopify-xr').then(setupXR).catch(() => {});
  };

  const initializeProductMedia = (root) => {
    if (root.dataset.productMediaInitialized === 'true') return;
    root.dataset.productMediaInitialized = 'true';

    root.querySelectorAll('[data-deferred-media-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const container = button.closest('[data-deferred-media]');
        const media = button.closest('[data-media-id]');
        if (!container || !media) return;

        setActiveThumbnail(root, media.dataset.mediaId);
        activateDeferredMedia(root, container);
        announceMedia(root, media);
      });
    });

    root.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      thumbnail.addEventListener('click', (event) => {
        event.preventDefault();
        selectMedia(root, thumbnail.dataset.targetMediaId);
      });
    });

    root.addEventListener(
      'play',
      (event) => {
        if (event.target instanceof HTMLMediaElement) pauseAllMedia(root, event.target);
      },
      true
    );

    initializeShopifyXR(root);
  };

  const initializeProduct = (root) => {
    if (!root || root.dataset.productInitialized === 'true') return;

    root.dataset.productInitialized = 'true';
    initializeProductMedia(root);

    const variantSelect = root.querySelector('[data-variant-select]');
    if (!variantSelect) return;

    const price = root.querySelector('[data-product-price]');
    const comparePrice = root.querySelector('[data-product-compare-price]');
    const unitPrice = root.querySelector('[data-product-unit-price]');
    const status = root.querySelector('[data-product-status]');
    const quantity = root.querySelector('[data-quantity-input]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addButtonText = root.querySelector('[data-add-to-cart-text]');
    const dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
    const xrButton = root.querySelector('[data-shopify-xr]');

    const selectVariant = () => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;

      const isAvailable = option.dataset.available === 'true';
      root.dataset.variantId = option.dataset.variantId || '';

      updateText(price, option.dataset.price || '');

      if (comparePrice) {
        updateText(comparePrice, option.dataset.comparePrice || '');
        comparePrice.hidden = !option.dataset.comparePrice;
      }

      if (unitPrice) {
        updateText(unitPrice, option.dataset.unitPrice || '');
        unitPrice.hidden = !option.dataset.unitPrice;
      }

      if (status) {
        status.replaceChildren();
        if (isAvailable) {
          const dot = document.createElement('span');
          dot.className = 'ramiu-product__availability-dot';
          dot.setAttribute('aria-hidden', 'true');
          status.append(dot, document.createTextNode(status.dataset.inStockLabel || 'In stock'));
        } else {
          status.textContent = status.dataset.soldOutLabel || 'Sold out';
        }
      }

      if (quantity) {
        const minimum = Number(option.dataset.quantityMin || 1);
        const increment = Number(option.dataset.quantityIncrement || 1);
        const maximum = option.dataset.quantityMax;

        quantity.min = String(minimum);
        quantity.step = String(increment);
        if (maximum) quantity.max = maximum;
        else quantity.removeAttribute('max');

        const currentQuantity = quantity.valueAsNumber;
        const violatesIncrement =
          Number.isFinite(currentQuantity) && (currentQuantity - minimum) % increment !== 0;

        if (!Number.isFinite(currentQuantity) || currentQuantity < minimum || violatesIncrement) {
          quantity.value = String(minimum);
        } else if (maximum && currentQuantity > Number(maximum)) {
          quantity.value = maximum;
        }
      }

      if (addButton) {
        addButton.disabled = !isAvailable;
        updateText(
          addButtonText,
          isAvailable ? addButton.dataset.addLabel : addButton.dataset.soldOutLabel
        );
      }

      if (dynamicCheckout) dynamicCheckout.hidden = !isAvailable;

      if (option.dataset.mediaId) {
        const shouldScroll = window.matchMedia('(max-width: 989px)').matches;
        const selectedMedia = selectMedia(root, option.dataset.mediaId, {
          activate: false,
          scroll: shouldScroll,
        });

        if (xrButton && selectedMedia?.dataset.mediaType === 'model') {
          xrButton.dataset.shopifyModel3dId = option.dataset.mediaId;
          window.ShopifyXR?.setupXRElements();
        }
      }

      if (window.history?.replaceState && option.dataset.variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.dataset.variantId);
        window.history.replaceState({}, '', url);
      }

      root.dispatchEvent(
        new CustomEvent('ramiu:variant-change', {
          bubbles: true,
          detail: {
            productId: root.dataset.productId,
            sectionId: root.dataset.sectionId,
            variantId: option.dataset.variantId,
            available: isAvailable,
          },
        })
      );
    };

    variantSelect.addEventListener('change', selectVariant);

    const initialOption = variantSelect.selectedOptions[0];
    if (initialOption?.dataset.mediaId) setActiveThumbnail(root, initialOption.dataset.mediaId);
  };

  const initializeWithin = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initializeProduct(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeProduct);
  };

  if (!window.RamiuProduct) {
    window.RamiuProduct = { initializeWithin, pauseAllMedia };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
    } else {
      initializeWithin(document);
    }

    document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  } else {
    window.RamiuProduct.initializeWithin(document);
  }
})();
